import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Multi-project hub context (docs: multi-project hub plan, §4.3).
 *
 * A hub is its own entity served by `/api/hubs`, rendered at `/hub/{hubId}`.
 * After the hub's Welcome the guest picks a member on `/hub/{hubId}/mode-select`;
 * the booth then navigates to `/{memberId}` as if it had booted into that
 * project. This store is the only thing that remembers the hand-off:
 *
 *   - which hub the current member was entered from, so every session exit
 *     (`getHomePath`) returns to the HUB Welcome instead of the member's;
 *   - a one-shot `pendingAutoStart` flag the member Welcome consumes to start
 *     the session on mount — the guest already tapped Start on the hub and
 *     picked a mode; a second Welcome is the bug the request complains about.
 *
 * Persisted because a reload / renderer crash mid-session must still return to
 * the hub. Cleared when the operator leaves the booth (Projects page) or when a
 * project that is neither the hub nor its member loads (`syncWithProject`).
 */
export interface HubState {
  hubId?: string
  memberProjectId?: string
  /** Set on pick, consumed exactly once by the member Welcome. */
  pendingAutoStart: boolean
  /**
   * Where a finished session returns to: the hub Welcome, or straight back to
   * the picker. Parked here at pick time because by the time the session ends
   * the loaded config is the MEMBER's, which knows nothing about the hub.
   *
   * A hub-level setting, so unlike the member fields it survives returning to
   * the same hub — only leaving the hub clears it.
   */
  returnTo?: 'welcome' | 'mode'
  /** Epoch ms of the pick. A stale flag (reload long after the pick) is dropped, not fired. */
  enteredAt?: number
  enter: (
    hubId: string,
    memberProjectId: string,
    opts?: { returnTo?: 'welcome' | 'mode' }
  ) => void
  /** Atomically take the auto-start ticket. Returns true only for the first caller. */
  consumeAutoStart: () => boolean
  /**
   * Reconcile with the project that just loaded: the recorded member keeps
   * everything; any other project clears the context — an operator who opens a
   * member directly from the list gets a standalone booth.
   */
  syncWithProject: (projectId: string | undefined) => void
  /**
   * Reconcile with the hub route that just loaded. Arriving back on the
   * recorded hub drops the member and the ticket so the next guest starts
   * fresh; arriving on a DIFFERENT hub clears everything, otherwise a booth
   * switched between hubs would keep returning members to the old one.
   *
   * Separate from `syncWithProject` because a hub route has no `:projectId`
   * param at all — passing `undefined` there is a no-op by design.
   */
  syncWithHub: (hubId: string | undefined) => void
  clear: () => void
}

/** Auto-start tickets older than this are stale (crash/reload) and are dropped. */
export const HUB_AUTO_START_MAX_AGE_MS = 60_000

export const useHubStore = create<HubState>()(
  persist(
    (set, get) => ({
      hubId: undefined,
      memberProjectId: undefined,
      pendingAutoStart: false,
      enteredAt: undefined,
      enter: (hubId, memberProjectId, opts): void => {
        set({
          hubId,
          memberProjectId,
          returnTo: opts?.returnTo,
          pendingAutoStart: true,
          enteredAt: Date.now()
        })
      },
      consumeAutoStart: (): boolean => {
        const { pendingAutoStart, enteredAt } = get()
        if (!pendingAutoStart) return false
        set({ pendingAutoStart: false })
        const fresh =
          typeof enteredAt === 'number' && Date.now() - enteredAt <= HUB_AUTO_START_MAX_AGE_MS
        return fresh
      },
      syncWithProject: (projectId): void => {
        const { hubId, memberProjectId } = get()
        if (!projectId || !hubId) return
        if (projectId === memberProjectId) return
        get().clear()
      },
      syncWithHub: (routeHubId): void => {
        const { hubId, memberProjectId, pendingAutoStart } = get()
        if (!routeHubId || !hubId) return
        if (routeHubId !== hubId) {
          get().clear()
          return
        }
        if (memberProjectId !== undefined || pendingAutoStart) {
          set({
            memberProjectId: undefined,
            pendingAutoStart: false,
            enteredAt: undefined
          })
        }
      },
      clear: (): void => {
        set({
          hubId: undefined,
          memberProjectId: undefined,
          returnTo: undefined,
          pendingAutoStart: false,
          enteredAt: undefined
        })
      }
    }),
    {
      // v2, no migration: every persisted v1 value is a PROJECT id that used to
      // double as a hub id. Those hubs are gone — carrying one forward would
      // send a finished member session to `/hub/<a project id>`, i.e. a dead
      // route. Dropping the key costs at most one guest's return-to-hub.
      name: 'hub-context-v2',
      partialize: (state) => ({
        hubId: state.hubId,
        memberProjectId: state.memberProjectId,
        returnTo: state.returnTo,
        pendingAutoStart: state.pendingAutoStart,
        enteredAt: state.enteredAt
      })
    }
  )
)
